import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {InputChangeEvent} from 'util/Types';

import type {PredictionJobSearchFilters} from './usePredictionJobSearch';

const DEBOUNCE_MS = 300;

interface PredictionJobFiltersProps {
  filters: PredictionJobSearchFilters;
  onFiltersChange: (filters: PredictionJobSearchFilters) => void;
  predictionBatchLabel: string | null;
  onClearBatchFilter: () => void;
}

export default function PredictionJobFilters({
  filters,
  onFiltersChange,
  predictionBatchLabel,
  onClearBatchFilter,
}: PredictionJobFiltersProps): JSX.Element {
  const [queryText, setQueryText] = React.useState(filters.query);

  const filtersRef = React.useRef(filters);
  filtersRef.current = filters;
  const onFiltersChangeRef = React.useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (queryText !== filtersRef.current.query) {
        onFiltersChangeRef.current({...filtersRef.current, query: queryText});
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [queryText]);

  React.useEffect(() => {
    setQueryText(filters.query);
  }, [filters.query]);

  const handleQueryChange = React.useCallback((e: InputChangeEvent) => {
    setQueryText(e.target.value);
  }, []);

  const handleQueryClear = React.useCallback(() => {
    setQueryText('');
    onFiltersChange({...filters, query: ''});
  }, [filters, onFiltersChange]);

  return (
    <div
      className={css(styles.filtersRow)}
      {...getTestProps({component: 'PredictionJobFilters', element: 'root'})}
    >
      <PlainSearchInput
        className={css(styles.searchInput)}
        clearType={ClearType.X}
        onChange={handleQueryChange}
        onEscape={handleQueryClear}
        placeholder="Search prediction jobs..."
        value={queryText}
      />
      {predictionBatchLabel != null && (
        <Chip
          size={ChipSize.SMALL}
          text={`Prediction Batch: ${predictionBatchLabel}`}
          onRemove={onClearBatchFilter}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  filtersRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  searchInput: {
    marginRight: 10,
    marginTop: 6,
    marginBottom: 6,
    width: 300,
    verticalAlign: 'middle',
  },
});
